import { useState, type ReactNode } from 'react';
import { DisplayNameInput } from './DisplayNameInput';
import { ErrorBanner } from './ErrorBanner';
import { useT } from '../i18n';
import '../styles/screens.css';

interface JoinRoomScreenProps {
  onJoinRoom: (roomCode: string, displayName: string, asSpectator?: boolean) => void;
  error: string | null;
  clearError: () => void;
  onBack: () => void;
  /** Pre-fill the room code (e.g., from a shared link). */
  initialRoomCode?: string;
}

/**
 * Error codes returned by validateRoomCode.
 * Components map these to translated messages via the i18n system.
 */
export type RoomCodeError = 'required' | 'length' | 'invalid_chars';

const ROOM_CODE_ERROR_KEYS: Record<RoomCodeError, string> = {
  required: 'roomCode.errorRequired',
  length: 'roomCode.errorLength',
  invalid_chars: 'roomCode.errorInvalidChars',
};

/**
 * Validates a room code against the server's allowed character set.
 * Room codes are 6 characters from A-HJ-NP-Z2-9 (no 0, O, 1, I).
 * The input is case-insensitive — uppercase on submission.
 * Returns an error code (not a translated message) for i18n.
 */
export function validateRoomCode(code: string): RoomCodeError | null {
  const trimmed = code.trim().toUpperCase();
  if (trimmed.length === 0) return 'required';
  if (trimmed.length !== 6) return 'length';
  if (!/^[A-HJ-NP-Z2-9]{6}$/.test(trimmed)) {
    return 'invalid_chars';
  }
  return null;
}

/**
 * Normalize room code: trim, uppercase, strip non-alphanumeric characters.
 */
export function normalizeRoomCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function JoinRoomScreen({
  onJoinRoom,
  error,
  clearError,
  onBack,
  initialRoomCode = '',
}: JoinRoomScreenProps): ReactNode {
  const t = useT();
  const [roomCode, setRoomCode] = useState(initialRoomCode);
  const [displayName, setDisplayName] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [asSpectator, setAsSpectator] = useState(false);

  const codeErrorCode = codeTouched ? validateRoomCode(roomCode) : null;
  const codeError = codeErrorCode ? t(ROOM_CODE_ERROR_KEYS[codeErrorCode] as any) : null;
  const canSubmit =
    !codeErrorCode && roomCode.trim().length > 0 && displayName.trim().length >= 2;

  const handleJoin = () => {
    if (!canSubmit) return;
    const normalized = normalizeRoomCode(roomCode);
    onJoinRoom(normalized, displayName.trim(), asSpectator);
  };

  const handleCodeChange = (value: string) => {
    // During IME composition (e.g., CJK input), skip regex filtering
    // to avoid corrupting the composition session in Safari
    if (isComposing) {
      setRoomCode(value.slice(0, 6));
    } else {
      // Only allow valid characters during input for better UX
      const filtered = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 6);
      setRoomCode(filtered);
    }
    if (error) clearError();
  };

  return (
    <div className="screen-container">
      <h1 className="screen-title">{t('joinRoom.title')}</h1>

      <div className="field-group">
        <label className="field-label" htmlFor="room-code">
          {t('joinRoom.roomCodeLabel')}
        </label>
        <input
          id="room-code"
          type="text"
          value={roomCode}
          onChange={(e) => handleCodeChange(e.target.value)}
          onBlur={() => setCodeTouched(true)}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={(e) => {
            setIsComposing(false);
            // Process the final composed value
            handleCodeChange((e.target as HTMLInputElement).value);
          }}
          placeholder={t('joinRoom.roomCodePlaceholder')}
          className={'field-input' + (codeError ? ' field-input--error' : '')}
          maxLength={6}
          autoComplete="off"
          autoCapitalize="characters"
          style={{ fontFamily: 'monospace', letterSpacing: '0.15em', textTransform: 'uppercase' }}
        />
        {codeError && (
          <p className="field-hint field-hint--error">{codeError}</p>
        )}
        {!codeError && (
          <p className="field-hint">{t('joinRoom.roomCodeHint')}</p>
        )}
      </div>

      <div className="field-group">
        <DisplayNameInput
          value={displayName}
          onChange={(v) => {
            setDisplayName(v);
            if (error) clearError();
          }}
        />
      </div>

      <ErrorBanner message={error} onDismiss={clearError} />

      <div className="field-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
        <input
          id="join-as-spectator"
          type="checkbox"
          checked={asSpectator}
          onChange={(e) => setAsSpectator(e.target.checked)}
          style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
        />
        <label htmlFor="join-as-spectator" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
          {t('joinRoom.joinAsSpectator')}
        </label>
      </div>

      <div className="btn-row">
        <button className="btn btn--link" onClick={onBack}>
          {t('joinRoom.back')}
        </button>
        <button
          className="btn btn--primary"
          onClick={handleJoin}
          disabled={!canSubmit}
        >
          {t('joinRoom.joinRoom')}
        </button>
      </div>
    </div>
  );
}
